import { Helmet } from "react-helmet-async";

/**
 * StructuredData — emit a single JSON-LD <script> block in the document head.
 *
 * Use this for Organization / LodgingBusiness / FAQPage / BreadcrumbList /
 * Review nodes. The helper accepts either one object or an array, and adds
 * `@context` if missing. Pages should compose schemas at the top of their
 * tree alongside their <Helmet> so search engines see structured data on
 * the very first paint.
 */
export default function StructuredData({ data, id }) {
  if (!data) return null;
  const blocks = Array.isArray(data) ? data : [data];
  const enriched = blocks.map((b) => (b && !b["@context"]) ? { "@context": "https://schema.org", ...b } : b);
  const json = JSON.stringify(enriched.length === 1 ? enriched[0] : enriched);
  return (
    <Helmet>
      <script type="application/ld+json" data-testid={id || "structured-data"}>{json}</script>
    </Helmet>
  );
}

// Reusable Organization snippet — referenced from the homepage and a few
// SEO landing pages so the brand entity is consistent across the site.
export const orgSchema = ({ siteUrl = "https://vyagram.com", logo = "https://vyagram.com/vyagram-logo.png", phone, email, sameAs = [] } = {}) => ({
  "@type": "Organization",
  name: "Vyagram Experiences",
  url: siteUrl,
  logo,
  description: "Design-led stays in South Delhi and Gurugram for couples, work trips, shoots, private gatherings, and longer escapes.",
  ...(phone || email ? {
    contactPoint: [{
      "@type": "ContactPoint",
      contactType: "reservations",
      ...(phone && { telephone: phone }),
      ...(email && { email }),
      areaServed: "IN",
      availableLanguage: ["en", "hi"],
    }],
  } : {}),
  ...(sameAs.length ? { sameAs } : {}),
});

export const faqSchema = (items) => ({
  "@type": "FAQPage",
  mainEntity: (items || []).map((it) => ({
    "@type": "Question",
    name: it.q || it.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: it.a || it.answer,
    },
  })),
});

export const breadcrumbSchema = (crumbs) => ({
  "@type": "BreadcrumbList",
  itemListElement: (crumbs || []).map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: c.url,
  })),
});

export const lodgingSchema = (property, baseUrl = "https://vyagram.com") => {
  if (!property) return null;
  const reviewCount = property.review_count || property.stats_total_reviews || 0;
  const rating = Number(property.rating || property.stats_avg_rating || 0);
  const aggregate = (reviewCount > 0 && rating > 0) ? {
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: rating.toFixed(2),
      reviewCount,
      bestRating: 5,
      worstRating: 1,
    },
  } : {};
  return {
    "@type": "LodgingBusiness",
    name: property.display_name,
    description: property.short_description || property.tagline,
    image: [property.hero_image, ...(property.gallery || [])].filter(Boolean).slice(0, 6),
    url: `${baseUrl}/stays/${property.slug}`,
    address: {
      "@type": "PostalAddress",
      addressLocality: property.location_label || "South Delhi",
      addressRegion: "DL",
      addressCountry: "IN",
    },
    priceRange: `₹${Number(property.nightly_price_from || 0).toLocaleString("en-IN")}+`,
    numberOfRooms: property.bedrooms,
    occupancy: { "@type": "QuantitativeValue", maxValue: property.guests },
    ...aggregate,
  };
};

export const reviewSchema = (review, propertyName, baseUrl = "https://vyagram.com") => ({
  "@type": "Review",
  reviewRating: {
    "@type": "Rating",
    ratingValue: review.rating,
    bestRating: 5,
    worstRating: 1,
  },
  author: { "@type": "Person", name: review.reviewer_first_name || "Guest" },
  datePublished: review.review_month_year || "",
  reviewBody: review.quote,
  itemReviewed: {
    "@type": "LodgingBusiness",
    name: propertyName || "Vyagram Stay",
    url: baseUrl,
  },
});
